import * as React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { Plus, Trash2, X, Mail, MessageSquare, Monitor, BellRing, RotateCcw, Check, Minus, Battery, Link2, CircleSlash, ScrollText } from "lucide-react";
import { Modal } from "../../../ui/components/Modal";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Avatar } from "../../../ui/components/Avatar";
import { UnsavedChangesChip } from "../../../ui/components/UnsavedChangesChip";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import * as pickerStyles from "../users/AddUserToOrganizationModal.css";
import {
  linkifyHistorySummary,
  type HistoryEntityLink,
} from "../../../ui/components/ListView/logSummaryLinks";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import { fetchProductImages } from "../../lib/profile-avatar-upload";
import * as subscriptionsApi from "../../lib/api/subscriptions";
import type {
  SubscriptionBatchItem,
  MinSeverity,
  ProductAlertPrefs,
} from "../../lib/api/subscriptions";
import * as systemEventsApi from "../../lib/api/system-events";
import type { SystemEvent } from "../../lib/api/system-events";
import * as adminUsersApi from "../../lib/api/admin-users";
import type { AdminUserListItem } from "../../lib/api/admin-users";
import * as productsApi from "../../lib/api/products";
import type { DataCatalogAlert } from "../../lib/api/products";
import * as s from "./ProductSubscriptionsModal.css";
import { SubscriptionNotificationHistoryModal } from "./SubscriptionNotificationHistoryModal";
import { ProductAlertPrefsPanel } from "./ProductAlertPrefsPanel";
import {
  ALERT_CLOSED_KEY,
  ALERT_EXTENDED_KEY,
  ALERT_OPENED_KEY,
  COMPOSITE_ALERT_DESCRIPTION,
  COMPOSITE_ALERT_PICKER_KEY,
  COMPOSITE_ALERT_SUMMARY_TEMPLATE,
  COMPOSITE_ALERT_TITLE,
  DEFAULT_ALERT_ENABLED_SEVERITIES,
  alertPrefsEqual,
  cloneAlertPrefs,
  isAlertEventKey,
  type AlertPrefsDraft,
} from "./productAlertSubscription";

export interface ProductSubscriptionsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  productId: string;
  productName: string;
  owningOrganizationId?: string | null;
  initialSubscriberUserId?: string;
  /** Display label when user is locked (e.g. from Subscribers edit). */
  initialSubscriberLabel?: string;
  lockUser?: boolean;
  lockProduct?: boolean;
  /**
   * When true, open with an empty user picker (do not default to the signed-in user).
   * Used by "+ Add subscription" on the product subscribers section.
   */
  skipUserPrefill?: boolean;
  /**
   * How to phrase summaries:
   * - `me` → "Notify me whenever…"
   * - `named` → "Notify Chase whenever…"
   */
  summaryPerspective?: "me" | "named";
  /** Close modal and jump to product access section on the detail page. */
  onGoToProductAccess?: () => void;
  onSaved?: () => void;
}

interface DraftSubscription extends SubscriptionBatchItem {
  id: string;
  eventLabel: string;
  eventDescription?: string | null;
  summary?: string | null;
  usesSeverity: boolean;
  /** True for rows that exist only in the draft (not yet saved). */
  isNew?: boolean;
  /** Soft-removed existing subscription; excluded on save. */
  pendingRemoval?: boolean;
}

type BaselineSubscription = {
  systemEventKey: string;
  notifyEmail: boolean;
  notifySms: boolean;
  notifyWeb: boolean;
  notifyPush: boolean;
  minSeverity: MinSeverity;
};

function toBaseline(drafts: DraftSubscription[]): BaselineSubscription[] {
  return drafts
    .filter((d) => !d.pendingRemoval)
    .map((d) => ({
      systemEventKey: d.systemEventKey,
      notifyEmail: d.notifyEmail,
      notifySms: d.notifySms,
      notifyWeb: d.notifyWeb,
      notifyPush: d.notifyPush,
      minSeverity: d.usesSeverity ? d.minSeverity : "all",
    }))
    .sort((a, b) => a.systemEventKey.localeCompare(b.systemEventKey));
}

function computeDirtyCount(
  drafts: DraftSubscription[],
  baseline: BaselineSubscription[],
  alertPrefs: AlertPrefsDraft | null,
  baselineAlertPrefs: AlertPrefsDraft | null,
  alertSubscribed: boolean,
): number {
  const baselineByKey = new Map(baseline.map((b) => [b.systemEventKey, b]));
  const draftActive = drafts.filter((d) => !d.pendingRemoval);
  const draftKeys = new Set(draftActive.map((d) => d.systemEventKey));

  let count = 0;
  for (const d of drafts) {
    if (d.pendingRemoval && baselineByKey.has(d.systemEventKey)) {
      count += 1;
      continue;
    }
    if (d.pendingRemoval) continue;
    const base = baselineByKey.get(d.systemEventKey);
    if (!base || d.isNew) {
      count += 1;
      continue;
    }
    if (d.notifyEmail !== base.notifyEmail) count += 1;
    if (d.notifySms !== base.notifySms) count += 1;
    if (d.notifyWeb !== base.notifyWeb) count += 1;
    if (d.notifyPush !== base.notifyPush) count += 1;
    if (d.usesSeverity && !isAlertEventKey(d.systemEventKey) && d.minSeverity !== base.minSeverity) {
      count += 1;
    }
  }
  for (const b of baseline) {
    if (!draftKeys.has(b.systemEventKey) && !drafts.some((d) => d.systemEventKey === b.systemEventKey)) {
      count += 1;
    }
  }

  if (alertSubscribed) {
    const current = alertPrefs ?? {
      enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
      overrides: [],
    };
    const basePrefs = baselineAlertPrefs ?? {
      enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
      overrides: [],
    };
    if (!alertPrefsEqual(current, basePrefs)) count += 1;
  } else if (baselineAlertPrefs) {
    count += 1;
  }

  return count;
}


const SEVERITY_OPTIONS: { value: MinSeverity; label: string }[] = [
  { value: "all", label: "All" },
  { value: "low", label: "Low+" },
  { value: "medium", label: "Medium+" },
  { value: "high", label: "High+" },
  { value: "critical", label: "Critical" },
];

/** Subscription-picker titles (event names); independent of admin catalog labels. */
const PRODUCT_EVENT_PICKER_TITLES: Record<string, string> = {
  "product.decommissioned": "Product Decommissioning",
  "product.deleted": "Product Deletion",
  "product.details_changed": "Product Details Changes",
  "product.settings_changed": "Product Settings Changes",
  [ALERT_OPENED_KEY]: COMPOSITE_ALERT_TITLE,
  [ALERT_CLOSED_KEY]: COMPOSITE_ALERT_TITLE,
};

/**
 * Completes “Notify me when…”. Keep present tense so every option reads naturally.
 */
const PRODUCT_EVENT_PICKER_DESCRIPTIONS: Record<string, string> = {
  "product.decommissioned": "The product is decommissioned",
  "product.deleted": "The product is deleted",
  "product.details_changed": "The product details change",
  "product.settings_changed": "The product settings change",
  [ALERT_OPENED_KEY]: COMPOSITE_ALERT_DESCRIPTION,
  [ALERT_CLOSED_KEY]: COMPOSITE_ALERT_DESCRIPTION,
};

type PickerItem =
  | { kind: "event"; event: SystemEvent }
  | { kind: "composite"; key: typeof COMPOSITE_ALERT_PICKER_KEY };

function pickerTitleFor(event: Pick<SystemEvent, "key" | "label">) {
  return PRODUCT_EVENT_PICKER_TITLES[event.key] ?? event.label;
}

function pickerDescriptionFor(event: Pick<SystemEvent, "key" | "description">) {
  return PRODUCT_EVENT_PICKER_DESCRIPTIONS[event.key] ?? event.description ?? null;
}

function authUserAsListItem(user: {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  phone?: string | null;
}): AdminUserListItem {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    avatarUrl: user.avatarUrl ?? null,
    isEmailVerified: Boolean(user.emailVerified),
    isPhoneVerified: Boolean(user.phoneVerified),
    phone: user.phone ?? undefined,
  };
}

function channelAvailability(user: AdminUserListItem | null): {
  email: boolean;
  sms: boolean;
} {
  if (!user) return { email: false, sms: false };
  return {
    email: Boolean(user.isEmailVerified),
    sms: Boolean(user.isPhoneVerified) && Boolean(user.phone?.trim()),
  };
}

function userDisplayName(u: Pick<AdminUserListItem, "firstName" | "lastName" | "email">) {
  return `${u.firstName} ${u.lastName}`.trim() || u.email;
}

function userFirstName(u: Pick<AdminUserListItem, "firstName" | "email">) {
  return u.firstName?.trim() || u.email.split("@")[0] || "user";
}

function buildSummary(template: string | null | undefined, entityLabel: string, eventLabel: string, subscriberLabel: string) {
  if (template?.trim()) {
    return template
      .split("{entityLabel}").join(entityLabel)
      .split("{subscriberLabel}").join(subscriberLabel)
      .split("{eventLabel}").join(eventLabel);
  }
  return `Notify ${subscriberLabel} whenever ${entityLabel} (${eventLabel})`;
}

function SubscriptionSummaryText({
  summary,
  links,
  onEntityClick,
}: {
  summary: string;
  links: HistoryEntityLink[];
  onEntityClick: (link: HistoryEntityLink) => void;
}) {
  if (!summary) return null;
  if (links.length === 0) {
    return (
      <span className={s.notificationSummary} title={summary}>
        {summary}
      </span>
    );
  }
  return (
    <span className={s.notificationSummary} title={summary}>
      {linkifyHistorySummary(summary, links, (link, key) => (
        <button
          key={key}
          type="button"
          className={s.summaryLink}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEntityClick(link);
          }}
        >
          {link.label}
        </button>
      ))}
    </span>
  );
}

function ChannelToggle({
  label,
  active,
  dirty,
  disabled,
  unavailable,
  unavailableReason,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  dirty?: boolean;
  disabled?: boolean;
  unavailable?: boolean;
  unavailableReason?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const title = unavailable
    ? unavailableReason || label
    : dirty
      ? `${label} (unsaved)`
      : label;
  return (
    <button
      type="button"
      className={clsx(
        s.channelToggle,
        dirty && !unavailable && s.dirtyControl,
        unavailable && s.channelToggleUnavailable,
      )}
      data-active={active && !unavailable ? "true" : "false"}
      data-unsaved={dirty && !unavailable ? "true" : undefined}
      data-unavailable={unavailable ? "true" : undefined}
      aria-pressed={active && !unavailable}
      aria-label={title}
      title={title}
      disabled={disabled || unavailable}
      onClick={onClick}
    >
      {children}
      {unavailable ? (
        <span className={s.channelToggleUnavailableBadge} aria-hidden>
          <CircleSlash size={9} strokeWidth={2.75} />
        </span>
      ) : active && dirty ? (
        <span className={s.channelToggleUnsavedBadge} aria-hidden>
          <Plus size={9} strokeWidth={3} />
        </span>
      ) : active ? (
        <span className={s.channelToggleCheck} aria-hidden>
          <Check size={9} strokeWidth={3} />
        </span>
      ) : dirty ? (
        <span className={s.channelToggleUnsavedBadge} aria-hidden>
          <Minus size={9} strokeWidth={3} />
        </span>
      ) : null}
    </button>
  );
}

export function ProductSubscriptionsModal({
  open,
  setOpen,
  productId,
  productName,
  owningOrganizationId,
  initialSubscriberUserId,
  initialSubscriberLabel,
  lockUser = false,
  lockProduct: _lockProduct = true,
  skipUserPrefill = false,
  summaryPerspective,
  onGoToProductAccess,
  onSaved,
}: ProductSubscriptionsModalProps) {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? localStorage.getItem("access_token");

  // Allow picking a user whenever the field is unlocked; API enforces RBAC on save.
  const canSearchUsers = !lockUser;

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draftSubscriptions, setDraftSubscriptions] = React.useState<DraftSubscription[]>([]);
  const [baselineSubscriptions, setBaselineSubscriptions] = React.useState<BaselineSubscription[]>([]);
  const [alertPrefs, setAlertPrefs] = React.useState<AlertPrefsDraft | null>(null);
  const [baselineAlertPrefs, setBaselineAlertPrefs] = React.useState<AlertPrefsDraft | null>(null);
  const [catalogAlerts, setCatalogAlerts] = React.useState<DataCatalogAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = React.useState(false);
  const [availableEvents, setAvailableEvents] = React.useState<SystemEvent[]>([]);
  const [addEventModalOpen, setAddEventModalOpen] = React.useState(false);
  const [historyModalOpen, setHistoryModalOpen] = React.useState(false);
  const [hasProductAccess, setHasProductAccess] = React.useState(true);

  const [selectedUser, setSelectedUser] = React.useState<AdminUserListItem | null>(null);
  const [userSearch, setUserSearch] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<AdminUserListItem[]>([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [productImageUrl, setProductImageUrl] = React.useState<string | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const subscriberUserId = selectedUser?.id ?? "";
  const channels = channelAvailability(selectedUser);
  const perspective: "me" | "named" =
    summaryPerspective ??
    (selectedUser && user?.id && selectedUser.id !== user.id ? "named" : "me");
  const subscriberLabel =
    perspective === "me" ? "me" : selectedUser ? userFirstName(selectedUser) : "me";

  const updateDropdownPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  const dropdownVisible = canSearchUsers && pickerOpen && !selectedUser;

  React.useEffect(() => {
    if (!dropdownVisible) {
      setDropdownPosition(null);
      return;
    }
    const run = () => requestAnimationFrame(updateDropdownPosition);
    run();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [dropdownVisible, updateDropdownPosition]);

  React.useEffect(() => {
    if (!dropdownVisible) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      setPickerOpen(false);
      setSearchResults([]);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [dropdownVisible]);

  React.useEffect(() => {
    if (!canSearchUsers || !pickerOpen || selectedUser || !token) {
      return;
    }
    let cancelled = false;
    const query = userSearch.trim();
    const timer = setTimeout(() => {
      void adminUsersApi
        .fetchUsersList(token, { page: 1, limit: 8, search: query || undefined })
        .then((res) => {
          if (!cancelled) setSearchResults(res.data);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        });
    }, query ? 200 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userSearch, canSearchUsers, pickerOpen, selectedUser, token]);

  // Load product thumbnail for the locked product field
  React.useEffect(() => {
    if (!open || !token || !productId) {
      setProductImageUrl(null);
      return;
    }
    let cancelled = false;
    setProductImageUrl(null);
    void fetchProductImages(token, productId).then((images) => {
      if (cancelled) return;
      const sorted = [...images].sort((a, b) => a.sortOrder - b.sortOrder);
      const first = sorted[0];
      setProductImageUrl(first?.thumbUrl ?? first?.url ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [open, token, productId]);

  // Prefill selected user when modal opens
  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setUserSearch("");
    setSearchResults([]);
    setPickerOpen(false);
    setAddEventModalOpen(false);
    setHistoryModalOpen(false);

    if (skipUserPrefill) {
      setSelectedUser(null);
      setDraftSubscriptions([]);
      setBaselineSubscriptions([]);
      setAlertPrefs(null);
      setBaselineAlertPrefs(null);
      setHasProductAccess(true);
      setLoading(false);
      return;
    }

    const targetId = initialSubscriberUserId ?? user?.id;
    if (user && targetId && user.id === targetId) {
      setSelectedUser(authUserAsListItem(user));
      return;
    }
    if (targetId && token) {
      void adminUsersApi
        .fetchUsersList(token, { page: 1, limit: 1, search: initialSubscriberLabel || targetId })
        .then((res) => {
          const match = res.data.find((u) => u.id === targetId) ?? res.data[0];
          if (match && match.id === targetId) {
            setSelectedUser(match);
          } else if (initialSubscriberLabel) {
            const [firstName = "", ...rest] = initialSubscriberLabel.split(" ");
            setSelectedUser({
              id: targetId,
              email: "",
              firstName,
              lastName: rest.join(" "),
            });
          } else {
            setSelectedUser({
              id: targetId,
              email: "",
              firstName: "User",
              lastName: "",
            });
          }
        })
        .catch(() => {
          setSelectedUser({
            id: targetId,
            email: "",
            firstName: initialSubscriberLabel?.split(" ")[0] || "User",
            lastName: initialSubscriberLabel?.split(" ").slice(1).join(" ") || "",
          });
        });
      return;
    }
    if (user) setSelectedUser(authUserAsListItem(user));
  }, [open, initialSubscriberUserId, initialSubscriberLabel, user, token, skipUserPrefill]);

  React.useEffect(() => {
    if (!open || !token || !subscriberUserId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDraftSubscriptions([]);
    setBaselineSubscriptions([]);
    setAlertPrefs(null);
    setBaselineAlertPrefs(null);

    void subscriptionsApi
      .checkSubscriptionProductAccess(token, {
        subscriberUserId,
        entityId: productId,
        owningOrganizationId,
      })
      .then(async (access) => {
        if (cancelled) return;
        setHasProductAccess(access.hasAccess);
        if (!access.hasAccess) {
          setDraftSubscriptions([]);
          setBaselineSubscriptions([]);
          setAlertPrefs(null);
          setBaselineAlertPrefs(null);
          setAvailableEvents([]);
          setLoading(false);
          return;
        }

        const [events, existing] = await Promise.all([
          systemEventsApi.fetchSubscribableProductEvents(token),
          subscriptionsApi.fetchSubscriptions(token, {
            subscriberUserId,
            entityType: "product",
            entityId: productId,
            entityLabel: productName,
            subscriberLabel,
          }),
        ]);
        if (cancelled) return;
        setAvailableEvents(events);
        const eventByKey = new Map(events.map((e) => [e.key, e]));
        const drafts: DraftSubscription[] = existing.subscriptions.map((sub) => {
          const event = eventByKey.get(sub.systemEventKey);
          const eventLabel = isAlertEventKey(sub.systemEventKey)
            ? COMPOSITE_ALERT_TITLE
            : event
              ? pickerTitleFor(event)
              : PRODUCT_EVENT_PICKER_TITLES[sub.systemEventKey] ?? sub.eventLabel;
          return {
            id: sub.id,
            systemEventKey: sub.systemEventKey,
            eventLabel,
            eventDescription: isAlertEventKey(sub.systemEventKey)
              ? COMPOSITE_ALERT_DESCRIPTION
              : sub.eventDescription,
            summary: isAlertEventKey(sub.systemEventKey)
              ? buildSummary(
                  COMPOSITE_ALERT_SUMMARY_TEMPLATE,
                  productName,
                  COMPOSITE_ALERT_TITLE,
                  subscriberLabel,
                )
              : buildSummary(
                  event?.subscriptionSummaryTemplate ?? null,
                  productName,
                  eventLabel,
                  subscriberLabel,
                ),
            usesSeverity: sub.usesSeverity ?? Boolean(event?.defaultSeverity),
            notifyEmail: sub.notifyEmail,
            notifySms: sub.notifySms,
            notifyWeb: sub.notifyWeb,
            notifyPush: sub.notifyPush ?? false,
            minSeverity: sub.minSeverity,
            isNew: false,
            pendingRemoval: false,
          };
        });

        // Ensure open+close pair exists whenever either alert subscription is present.
        const hasOpen = drafts.some((d) => d.systemEventKey === ALERT_OPENED_KEY);
        const hasClose = drafts.some((d) => d.systemEventKey === ALERT_CLOSED_KEY);
        if (hasOpen !== hasClose) {
          const seed = drafts.find((d) => isAlertEventKey(d.systemEventKey))!;
          const missingKey = hasOpen ? ALERT_CLOSED_KEY : ALERT_OPENED_KEY;
          drafts.push({
            id: `draft-alert-${missingKey}-sync`,
            systemEventKey: missingKey,
            eventLabel: COMPOSITE_ALERT_TITLE,
            eventDescription: COMPOSITE_ALERT_DESCRIPTION,
            summary: buildSummary(
              COMPOSITE_ALERT_SUMMARY_TEMPLATE,
              productName,
              COMPOSITE_ALERT_TITLE,
              subscriberLabel,
            ),
            usesSeverity: true,
            notifyEmail: seed.notifyEmail,
            notifySms: seed.notifySms,
            notifyWeb: seed.notifyWeb,
            notifyPush: seed.notifyPush,
            minSeverity: "all",
            isNew: true,
            pendingRemoval: false,
          });
        }

        setDraftSubscriptions(drafts);
        setBaselineSubscriptions(toBaseline(drafts.filter((d) => !d.isNew)));

        const prefsFromApi: AlertPrefsDraft | null = existing.alertPrefs
          ? cloneAlertPrefs(existing.alertPrefs)
          : drafts.some((d) => isAlertEventKey(d.systemEventKey))
            ? {
                enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
                overrides: [],
              }
            : null;
        setAlertPrefs(prefsFromApi);
        setBaselineAlertPrefs(prefsFromApi ? cloneAlertPrefs(prefsFromApi) : null);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoading(false);
          setError(getApiErrorMessage(e, t("pro.products.subscriptions.error.load")));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    token,
    subscriberUserId,
    productId,
    productName,
    subscriberLabel,
    owningOrganizationId,
    t,
  ]);

  // Load catalog alerts for Product Alert prefs panel
  React.useEffect(() => {
    if (!open || !token || !productId) {
      setCatalogAlerts([]);
      return;
    }
    let cancelled = false;
    setAlertsLoading(true);
    void (async () => {
      try {
        const [product, catalogs] = await Promise.all([
          productsApi.getProductById(token, productId),
          productsApi.fetchDataCatalogs(token),
        ]);
        if (cancelled) return;
        const match = productsApi.resolveDataCatalogForProduct(product, catalogs);
        if (!match) {
          setCatalogAlerts([]);
          return;
        }
        // Prefer dedicated alerts list; fall back to catalog detail.alerts.
        try {
          const alerts = await productsApi.fetchDataCatalogAlerts(token, match.id);
          if (cancelled) return;
          setCatalogAlerts(alerts);
        } catch {
          const detail = await productsApi.fetchDataCatalog(token, match.id);
          if (cancelled) return;
          setCatalogAlerts((detail.alerts ?? []).filter((a) => a.isActive !== false));
        }
      } catch {
        if (!cancelled) setCatalogAlerts([]);
      } finally {
        if (!cancelled) setAlertsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, productId]);

  // Rephrase summaries when perspective/user changes without reloading
  React.useEffect(() => {
    setDraftSubscriptions((prev) =>
      prev.map((d) => {
        if (isAlertEventKey(d.systemEventKey)) {
          return {
            ...d,
            eventLabel: COMPOSITE_ALERT_TITLE,
            summary: buildSummary(
              COMPOSITE_ALERT_SUMMARY_TEMPLATE,
              productName,
              COMPOSITE_ALERT_TITLE,
              subscriberLabel,
            ),
          };
        }
        const event = availableEvents.find((e) => e.key === d.systemEventKey);
        return {
          ...d,
          summary: buildSummary(
            event?.subscriptionSummaryTemplate ?? null,
            productName,
            d.eventLabel,
            subscriberLabel,
          ),
        };
      }),
    );
  }, [subscriberLabel, productName, availableEvents]);

  const makeAlertDraftPair = (channelsSeed: {
    notifyEmail: boolean;
    notifySms: boolean;
    notifyWeb: boolean;
    notifyPush: boolean;
  }): DraftSubscription[] => {
    const stamp = Date.now();
    return [ALERT_OPENED_KEY, ALERT_CLOSED_KEY].map((key, i) => ({
      id: `draft-alert-${key}-${stamp}-${i}`,
      systemEventKey: key,
      eventLabel: COMPOSITE_ALERT_TITLE,
      eventDescription: COMPOSITE_ALERT_DESCRIPTION,
      summary: buildSummary(
        COMPOSITE_ALERT_SUMMARY_TEMPLATE,
        productName,
        COMPOSITE_ALERT_TITLE,
        subscriberLabel,
      ),
      usesSeverity: true,
      notifyEmail: channelsSeed.notifyEmail,
      notifySms: channelsSeed.notifySms,
      notifyWeb: channelsSeed.notifyWeb,
      notifyPush: channelsSeed.notifyPush,
      minSeverity: "all" as MinSeverity,
      isNew: true,
      pendingRemoval: false,
    }));
  };

  const handleAddEvent = (pickerKey: string) => {
    if (pickerKey === COMPOSITE_ALERT_PICKER_KEY) {
      setDraftSubscriptions((prev) => {
        const withoutAlert = prev.filter((d) => !isAlertEventKey(d.systemEventKey));
        return [
          ...withoutAlert,
          ...makeAlertDraftPair({
            notifyEmail: channels.email,
            notifySms: false,
            notifyWeb: true,
            notifyPush: false,
          }),
        ];
      });
      setAlertPrefs({
        enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
        overrides: [],
      });
      setAddEventModalOpen(false);
      return;
    }

    const event = availableEvents.find((e) => e.key === pickerKey);
    if (!event) return;
    const eventLabel = pickerTitleFor(event);
    const newDraft: DraftSubscription = {
      id: `draft-${Date.now()}`,
      systemEventKey: event.key,
      eventLabel,
      eventDescription: event.description,
      summary: buildSummary(
        event.subscriptionSummaryTemplate,
        productName,
        eventLabel,
        subscriberLabel,
      ),
      usesSeverity: Boolean(event.defaultSeverity),
      notifyEmail: channels.email,
      notifySms: false,
      notifyWeb: true,
      notifyPush: false,
      minSeverity: (event.defaultSeverity as MinSeverity) ?? "all",
      isNew: true,
      pendingRemoval: false,
    };
    setDraftSubscriptions((prev) => [...prev, newDraft]);
    setAddEventModalOpen(false);
  };

  const handleRemoveAlertComposite = () => {
    setDraftSubscriptions((prev) => {
      const alertRows = prev.filter((d) => isAlertEventKey(d.systemEventKey));
      const allNew = alertRows.every((d) => d.isNew);
      if (allNew) {
        setAlertPrefs(null);
        return prev.filter((d) => !isAlertEventKey(d.systemEventKey));
      }
      const anyRemoved = alertRows.some((d) => d.pendingRemoval);
      return prev.map((d) =>
        isAlertEventKey(d.systemEventKey)
          ? { ...d, pendingRemoval: !anyRemoved }
          : d,
      );
    });
  };

  const handleRemoveEvent = (id: string) => {
    setDraftSubscriptions((prev) => {
      const target = prev.find((d) => d.id === id);
      if (!target) return prev;
      if (isAlertEventKey(target.systemEventKey)) {
        return prev;
      }
      if (target.isNew) {
        return prev.filter((d) => d.id !== id);
      }
      return prev.map((d) =>
        d.id === id ? { ...d, pendingRemoval: !d.pendingRemoval } : d,
      );
    });
  };

  const handleUpdateAlertChannels = (updates: Partial<DraftSubscription>) => {
    setDraftSubscriptions((prev) =>
      prev.map((d) => (isAlertEventKey(d.systemEventKey) ? { ...d, ...updates } : d)),
    );
  };

  const handleUpdateDraft = (id: string, updates: Partial<DraftSubscription>) => {
    setDraftSubscriptions((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...updates } : d)),
    );
  };

  const persistSubscriptions = async (
    prefsOverride?: AlertPrefsDraft | null,
  ): Promise<void> => {
    if (!token || !subscriberUserId) {
      throw new Error(t("pro.products.subscriptions.error.save"));
    }
    const active = draftSubscriptions.filter((d) => !d.pendingRemoval);
    const batch: SubscriptionBatchItem[] = active.map((d) => ({
      systemEventKey: d.systemEventKey,
      notifyEmail: channels.email ? d.notifyEmail : false,
      notifySms: channels.sms ? d.notifySms : false,
      notifyWeb: d.notifyWeb,
      notifyPush: d.notifyPush,
      minSeverity: isAlertEventKey(d.systemEventKey)
        ? "all"
        : d.usesSeverity
          ? d.minSeverity
          : "all",
    }));
    const wantsAlert = active.some((d) => isAlertEventKey(d.systemEventKey));
    const prefsSource =
      prefsOverride != null
        ? prefsOverride
        : alertPrefs ?? {
            enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
            overrides: [],
          };
    const prefsPayload: ProductAlertPrefs | null = wantsAlert
      ? cloneAlertPrefs(prefsSource)
      : null;

    await subscriptionsApi.saveSubscriptionsBatch(token, {
      subscriberUserId,
      entityType: "product",
      entityId: productId,
      owningOrganizationId,
      entityLabel: productName,
      subscriptions: batch,
      alertPrefs: prefsPayload,
    });

    const synced = active.map((d) => ({
      ...d,
      isNew: false,
      pendingRemoval: false,
    }));
    setDraftSubscriptions(synced);
    setBaselineSubscriptions(toBaseline(synced));
    setAlertPrefs(prefsPayload ? cloneAlertPrefs(prefsPayload) : null);
    setBaselineAlertPrefs(prefsPayload ? cloneAlertPrefs(prefsPayload) : null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !subscriberUserId || dirtyCount === 0) return;
    setSaving(true);
    setError(null);
    try {
      await persistSubscriptions();
      onSaved?.();
      setOpen(false);
    } catch (err) {
      setError(getApiErrorMessage(err, t("pro.products.subscriptions.error.save")));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAlertPrefs = async (prefs: AlertPrefsDraft) => {
    setSaving(true);
    setError(null);
    try {
      await persistSubscriptions(prefs);
      onSaved?.();
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        t("pro.products.subscriptions.error.save"),
      );
      setError(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  const alertSubscribed = draftSubscriptions.some(
    (d) => isAlertEventKey(d.systemEventKey) && !d.pendingRemoval,
  );

  const dirtyCount = React.useMemo(
    () =>
      computeDirtyCount(
        draftSubscriptions,
        baselineSubscriptions,
        alertPrefs,
        baselineAlertPrefs,
        alertSubscribed,
      ),
    [
      draftSubscriptions,
      baselineSubscriptions,
      alertPrefs,
      baselineAlertPrefs,
      alertSubscribed,
    ],
  );
  const isDirty = dirtyCount > 0;

  const baselineByKey = React.useMemo(
    () => new Map(baselineSubscriptions.map((b) => [b.systemEventKey, b])),
    [baselineSubscriptions],
  );

  const subscribedKeys = new Set(
    draftSubscriptions.filter((d) => !d.pendingRemoval).map((d) => d.systemEventKey),
  );

  const pickerItems = React.useMemo((): PickerItem[] => {
    const items: PickerItem[] = [];
    const alertActive =
      subscribedKeys.has(ALERT_OPENED_KEY) || subscribedKeys.has(ALERT_CLOSED_KEY);
    if (!alertActive) {
      items.push({ kind: "composite", key: COMPOSITE_ALERT_PICKER_KEY });
    }
    for (const event of availableEvents) {
      if (isAlertEventKey(event.key) || event.key === ALERT_EXTENDED_KEY) continue;
      if (subscribedKeys.has(event.key)) continue;
      items.push({ kind: "event", event });
    }
    return items;
  }, [availableEvents, subscribedKeys]);

  const displayDrafts = React.useMemo(() => {
    const alertDrafts = draftSubscriptions.filter((d) => isAlertEventKey(d.systemEventKey));
    const other = draftSubscriptions.filter((d) => !isAlertEventKey(d.systemEventKey));
    const items: Array<
      | { kind: "single"; draft: DraftSubscription }
      | { kind: "alert"; drafts: DraftSubscription[] }
    > = [];
    if (alertDrafts.length > 0) {
      items.push({ kind: "alert", drafts: alertDrafts });
    }
    for (const draft of other) {
      items.push({ kind: "single", draft });
    }
    return items;
  }, [draftSubscriptions]);

  const availableToAdd = pickerItems;

  const summaryLinksFor = React.useCallback(
    (summary: string): HistoryEntityLink[] => {
      const links: HistoryEntityLink[] = [];
      if (productName && summary.includes(productName)) {
        links.push({ kind: "product", id: productId, label: productName });
      }
      if (
        perspective === "named" &&
        selectedUser &&
        subscriberLabel !== "me" &&
        summary.includes(subscriberLabel)
      ) {
        links.push({
          kind: "user",
          id: selectedUser.id,
          label: subscriberLabel,
        });
      }
      return links;
    },
    [productId, productName, perspective, selectedUser, subscriberLabel],
  );

  const onEntityClick = React.useCallback(
    (link: HistoryEntityLink) => {
      if (link.kind === "user") {
        void navigate({ to: "/workspace/users/$userId", params: { userId: link.id } });
        setOpen(false);
        return;
      }
      if (link.kind === "product") {
        void navigate({
          to: "/workspace/products/$productId",
          params: { productId: link.id },
        });
        setOpen(false);
      }
    },
    [navigate, setOpen],
  );

  const lockedChipLabel =
    selectedUser
      ? userDisplayName(selectedUser)
      : initialSubscriberLabel || "";

  return (
    <>
      <Modal open={open} setOpen={setOpen} minWidth="720px" maxWidth="860px" closeOnClickOff>
          <Modal.Form onSubmit={handleSave}>
            <Modal.Header>
              <Text level="h2" weight="bold">
                {t("pro.products.subscriptions.modal.title")}
              </Text>
            </Modal.Header>
            <Modal.Body>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {error && <Callout variant="error">{error}</Callout>}

                <div className={s.linkedFieldsBox}>
                <div className={s.field}>
                  <label className={formStyles.label}>{t("pro.products.subscriptions.user")}</label>
                  {lockUser || !canSearchUsers ? (
                    <div className={s.comboTrigger} style={{ cursor: "default" }}>
                      {selectedUser || lockedChipLabel ? (
                        <span className={s.chip}>
                          <Avatar
                            email={selectedUser?.email ?? ""}
                            firstName={selectedUser?.firstName ?? ""}
                            lastName={selectedUser?.lastName ?? ""}
                            image={normalizeAvatarUrl(selectedUser?.avatarUrl) || undefined}
                            size={24}
                          />
                          <span className={s.chipLabel}>{lockedChipLabel}</span>
                        </span>
                      ) : (
                        <span className={s.comboPlaceholder}>
                          {t("pro.products.subscriptions.searchUser")}
                        </span>
                      )}
                    </div>
                  ) : selectedUser ? (
                    <div className={s.comboTrigger}>
                      <span className={s.chip}>
                        <Avatar
                          email={selectedUser.email}
                          firstName={selectedUser.firstName}
                          lastName={selectedUser.lastName}
                          image={normalizeAvatarUrl(selectedUser.avatarUrl) || undefined}
                          size={24}
                        />
                        <span className={s.chipLabel}>{userDisplayName(selectedUser)}</span>
                        <button
                          type="button"
                          className={s.chipClear}
                          aria-label={t("shared.filter.clear")}
                          onClick={() => {
                          setSelectedUser(null);
                          setUserSearch("");
                          setDraftSubscriptions([]);
                          setBaselineSubscriptions([]);
                          setAlertPrefs(null);
                          setBaselineAlertPrefs(null);
                          setLoading(false);
                          setPickerOpen(true);
                          requestAnimationFrame(() => searchInputRef.current?.focus());
                        }}
                        disabled={saving || loading}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    </div>
                  ) : (
                    <div
                      ref={triggerRef}
                      className={s.comboTrigger}
                      onClick={() => {
                        setPickerOpen(true);
                        searchInputRef.current?.focus();
                      }}
                    >
                      <input
                        ref={searchInputRef}
                        className={s.comboInput}
                        value={userSearch}
                        onChange={(e) => {
                          setUserSearch(e.target.value);
                          setPickerOpen(true);
                        }}
                        onFocus={() => setPickerOpen(true)}
                        placeholder={t("pro.products.subscriptions.searchUser")}
                        disabled={saving}
                      />
                    </div>
                  )}
                  {dropdownVisible &&
                    dropdownPosition &&
                    createPortal(
                      <div
                        ref={dropdownRef}
                        className={pickerStyles.pickerDropdown}
                        style={{
                          top: dropdownPosition.top,
                          left: dropdownPosition.left,
                          width: dropdownPosition.width,
                        }}
                        role="listbox"
                      >
                        {searchResults.length === 0 ? (
                          <div style={{ padding: "10px 12px" }}>
                            <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
                              {t("pro.products.subscriptions.noUsersFound")}
                            </Text>
                          </div>
                        ) : (
                          searchResults.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              role="option"
                              aria-selected={false}
                              className={s.optionRow}
                              onClick={() => {
                                setSelectedUser(u);
                                setSearchResults([]);
                                setPickerOpen(false);
                                setUserSearch("");
                              }}
                            >
                              <Avatar
                                email={u.email}
                                firstName={u.firstName}
                                lastName={u.lastName}
                                image={normalizeAvatarUrl(u.avatarUrl) || undefined}
                                size={28}
                              />
                              <span className={s.optionText}>
                                <span className={s.optionName}>{userDisplayName(u)}</span>
                                <span className={s.optionEmail}>{u.email}</span>
                              </span>
                            </button>
                          ))
                        )}
                      </div>,
                      document.body,
                    )}
                </div>

                <div
                  className={s.linkedFieldsConnector}
                  aria-hidden
                >
                  <span
                    className={s.linkedFieldsIcon}
                    data-active={
                      Boolean((selectedUser || lockedChipLabel) && productId && productName)
                        ? "true"
                        : "false"
                    }
                  >
                    <Link2 size={14} strokeWidth={2.25} />
                  </span>
                </div>

                <div className={s.field}>
                  <label className={formStyles.label}>{t("pro.products.subscriptions.product")}</label>
                  <div className={s.comboTrigger} style={{ cursor: "default" }}>
                    <span className={s.chip}>
                      <span className={s.productThumb} aria-hidden>
                        {productImageUrl ? (
                          <img src={productImageUrl} alt="" className={s.productThumbImg} />
                        ) : (
                          <Battery size={14} />
                        )}
                      </span>
                      <span className={s.chipLabel}>{productName}</span>
                    </span>
                  </div>
                </div>
                </div>

                <div>
                  <div className={s.sectionHeader}>
                    <div className={s.sectionHeaderLeft}>
                      <Text level="h3" weight="bold">
                        {t("pro.products.subscriptions.notifications")}
                      </Text>
                      {isDirty ? <UnsavedChangesChip count={dirtyCount} /> : null}
                    </div>
                    <div className={s.sectionHeaderActions}>
                      {subscriberUserId && productId ? (
                        <Button
                          type="button"
                          size="sm"
                          color="neutral"
                          variant="outline"
                          iconLeft={<ScrollText size={14} />}
                          onClick={() => setHistoryModalOpen(true)}
                          disabled={saving}
                        >
                          {t("pro.products.subscriptions.history")}
                        </Button>
                      ) : null}
                      {hasProductAccess &&
                      subscriberUserId &&
                      !loading &&
                      draftSubscriptions.some((d) => !d.pendingRemoval) ? (
                        <Button
                          type="button"
                          size="sm"
                          color="primary"
                          iconLeft={<Plus size={14} />}
                          onClick={() => setAddEventModalOpen(true)}
                          disabled={saving || availableToAdd.length === 0}
                        >
                          {t("pro.products.subscriptions.addSubscription")}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {!subscriberUserId ? (
                    <div className={s.emptyState}>
                      <Text level="body-small" className={s.emptyMessage}>
                        {t("pro.products.subscriptions.selectUserFirst")}
                      </Text>
                    </div>
                  ) : loading ? (
                    <div className={s.emptyState}>
                      <Text level="body-small" className={s.emptyMessage}>
                        {t("shared.common.loading")}
                      </Text>
                    </div>
                  ) : !hasProductAccess ? (
                    <div className={s.emptyState}>
                      <Text level="body-small" className={s.emptyMessage}>
                        {t("pro.products.subscriptions.noProductAccess")}
                      </Text>
                      {onGoToProductAccess ? (
                        <Button
                          type="button"
                          color="primary"
                          onClick={() => {
                            setOpen(false);
                            onGoToProductAccess();
                          }}
                        >
                          {t("pro.products.subscriptions.goToProductAccess")}
                        </Button>
                      ) : null}
                    </div>
                  ) : draftSubscriptions.length === 0 ? (
                    <div className={s.emptyState}>
                      <Text level="body-small" className={s.emptyMessage}>
                        {t("pro.products.subscriptions.noNotifications")}
                      </Text>
                      <Button
                        type="button"
                        color="primary"
                        onClick={() => setAddEventModalOpen(true)}
                        disabled={saving || availableToAdd.length === 0}
                      >
                        {t("pro.products.subscriptions.subscribe")}
                      </Button>
                    </div>
                  ) : (
                    <div className={s.notificationList}>
                      {displayDrafts.map((item) => {
                        if (item.kind === "alert") {
                          const drafts = item.drafts;
                          const primary =
                            drafts.find((d) => d.systemEventKey === ALERT_OPENED_KEY) ?? drafts[0];
                          const isAdded = drafts.every((d) => d.isNew);
                          const isRemoved = drafts.every((d) => d.pendingRemoval);
                          const baseOpen = baselineByKey.get(ALERT_OPENED_KEY);
                          const channelDirty = (key: keyof BaselineSubscription) =>
                            !isAdded &&
                            !isRemoved &&
                            Boolean(baseOpen) &&
                            primary[key as keyof DraftSubscription] !== baseOpen![key];
                          const prefsDirty =
                            !isAdded &&
                            !isRemoved &&
                            Boolean(baselineAlertPrefs) &&
                            Boolean(alertPrefs) &&
                            !alertPrefsEqual(alertPrefs!, baselineAlertPrefs!);
                          const channelsDirty =
                            Boolean(baseOpen) &&
                            (primary.notifyEmail !== baseOpen!.notifyEmail ||
                              primary.notifySms !== baseOpen!.notifySms ||
                              primary.notifyWeb !== baseOpen!.notifyWeb ||
                              primary.notifyPush !== baseOpen!.notifyPush);

                          return (
                            <div
                              key="product-alert-composite"
                              className={clsx(
                                s.notificationCard,
                                isAdded && s.notificationCardAdded,
                                isRemoved && s.notificationCardRemoved,
                              )}
                              data-unsaved={
                                isAdded || isRemoved || channelsDirty || prefsDirty
                                  ? "true"
                                  : undefined
                              }
                            >
                              <div className={s.notificationCardTop}>
                                <div className={s.notificationCopy}>
                                  <div className={s.titleRow}>
                                    <div
                                      className={s.notificationTitle}
                                      title={COMPOSITE_ALERT_TITLE}
                                    >
                                      {COMPOSITE_ALERT_TITLE}
                                    </div>
                                    {isAdded ? (
                                      <span className={s.changeBadge}>
                                        {t("pro.products.subscriptions.badge.added")}
                                      </span>
                                    ) : null}
                                    {isRemoved ? (
                                      <span className={s.changeBadge}>
                                        {t("pro.products.subscriptions.badge.removed")}
                                      </span>
                                    ) : null}
                                  </div>
                                  {primary.summary ? (
                                    <SubscriptionSummaryText
                                      summary={primary.summary}
                                      links={summaryLinksFor(primary.summary)}
                                      onEntityClick={onEntityClick}
                                    />
                                  ) : null}
                                </div>

                                <div className={s.notificationChannels}>
                                  <div className={s.notificationChannelToggles}>
                                    <ChannelToggle
                                      label={t("pro.products.subscriptions.channel.email")}
                                      active={primary.notifyEmail && channels.email}
                                      dirty={channelDirty("notifyEmail")}
                                      disabled={saving || isRemoved}
                                      unavailable={!channels.email}
                                      unavailableReason={t(
                                        "pro.products.subscriptions.channel.emailUnavailable",
                                      )}
                                      onClick={() =>
                                        handleUpdateAlertChannels({
                                          notifyEmail: !primary.notifyEmail,
                                        })
                                      }
                                    >
                                      <Mail size={14} />
                                    </ChannelToggle>
                                    <ChannelToggle
                                      label={t("pro.products.subscriptions.channel.sms")}
                                      active={primary.notifySms && channels.sms}
                                      dirty={channelDirty("notifySms")}
                                      disabled={saving || isRemoved}
                                      unavailable={!channels.sms}
                                      unavailableReason={
                                        selectedUser?.phone?.trim()
                                          ? t(
                                              "pro.products.subscriptions.channel.smsUnavailable",
                                            )
                                          : t("pro.products.subscriptions.channel.smsNoPhone")
                                      }
                                      onClick={() =>
                                        handleUpdateAlertChannels({
                                          notifySms: !primary.notifySms,
                                        })
                                      }
                                    >
                                      <MessageSquare size={14} />
                                    </ChannelToggle>
                                    <ChannelToggle
                                      label={t("pro.products.subscriptions.channel.web")}
                                      active={primary.notifyWeb}
                                      dirty={channelDirty("notifyWeb")}
                                      disabled={saving || isRemoved}
                                      onClick={() =>
                                        handleUpdateAlertChannels({
                                          notifyWeb: !primary.notifyWeb,
                                        })
                                      }
                                    >
                                      <Monitor size={14} />
                                    </ChannelToggle>
                                    <ChannelToggle
                                      label={t("pro.products.subscriptions.channel.push")}
                                      active={primary.notifyPush}
                                      dirty={channelDirty("notifyPush")}
                                      disabled={saving || isRemoved}
                                      onClick={() =>
                                        handleUpdateAlertChannels({
                                          notifyPush: !primary.notifyPush,
                                        })
                                      }
                                    >
                                      <BellRing size={14} />
                                    </ChannelToggle>
                                  </div>
                                </div>

                                <div className={s.notificationRemove}>
                                  <button
                                    type="button"
                                    className={s.removeButton}
                                    aria-label={
                                      isRemoved
                                        ? t("pro.products.subscriptions.undoRemove")
                                        : t("pro.products.subscriptions.remove")
                                    }
                                    title={
                                      isRemoved
                                        ? t("pro.products.subscriptions.undoRemove")
                                        : t("pro.products.subscriptions.remove")
                                    }
                                    disabled={saving}
                                    onClick={handleRemoveAlertComposite}
                                  >
                                    {isRemoved ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                                  </button>
                                </div>
                              </div>

                              {!isRemoved ? (
                                <ProductAlertPrefsPanel
                                  prefs={
                                    alertPrefs ?? {
                                      enabledSeverities: [...DEFAULT_ALERT_ENABLED_SEVERITIES],
                                      overrides: [],
                                    }
                                  }
                                  onChange={setAlertPrefs}
                                  baselinePrefs={
                                    isAdded || isRemoved ? null : baselineAlertPrefs
                                  }
                                  onSave={handleSaveAlertPrefs}
                                  alerts={catalogAlerts}
                                  alertsLoading={alertsLoading}
                                  disabled={saving}
                                  productName={productName}
                                />
                              ) : null}
                            </div>
                          );
                        }

                        const draft = item.draft;
                        const base = baselineByKey.get(draft.systemEventKey);
                        const isAdded = Boolean(draft.isNew);
                        const isRemoved = Boolean(draft.pendingRemoval);
                        const channelDirty = (key: keyof BaselineSubscription) =>
                          !isAdded &&
                          !isRemoved &&
                          Boolean(base) &&
                          draft[key as keyof DraftSubscription] !== base![key];

                        return (
                          <div
                            key={draft.id}
                            className={clsx(
                              s.notificationCard,
                              isAdded && s.notificationCardAdded,
                              isRemoved && s.notificationCardRemoved,
                            )}
                            data-unsaved={
                              isAdded ||
                              isRemoved ||
                              (base &&
                                (draft.notifyEmail !== base.notifyEmail ||
                                  draft.notifySms !== base.notifySms ||
                                  draft.notifyWeb !== base.notifyWeb ||
                                  draft.notifyPush !== base.notifyPush ||
                                  (draft.usesSeverity &&
                                    draft.minSeverity !== base.minSeverity)))
                                ? "true"
                                : undefined
                            }
                          >
                            <div className={s.notificationCardTop}>
                              <div className={s.notificationCopy}>
                                <div className={s.titleRow}>
                                  <div className={s.notificationTitle} title={draft.eventLabel}>
                                    {draft.eventLabel}
                                  </div>
                                  {isAdded ? (
                                    <span className={s.changeBadge}>
                                      {t("pro.products.subscriptions.badge.added")}
                                    </span>
                                  ) : null}
                                  {isRemoved ? (
                                    <span className={s.changeBadge}>
                                      {t("pro.products.subscriptions.badge.removed")}
                                    </span>
                                  ) : null}
                                </div>
                                {draft.summary ? (
                                  <SubscriptionSummaryText
                                    summary={draft.summary}
                                    links={summaryLinksFor(draft.summary)}
                                    onEntityClick={onEntityClick}
                                  />
                                ) : null}
                              </div>

                              <div className={s.notificationChannels}>
                                <div className={s.notificationChannelToggles}>
                                  <ChannelToggle
                                    label={t("pro.products.subscriptions.channel.email")}
                                    active={draft.notifyEmail && channels.email}
                                    dirty={channelDirty("notifyEmail")}
                                    disabled={saving || isRemoved}
                                    unavailable={!channels.email}
                                    unavailableReason={t(
                                      "pro.products.subscriptions.channel.emailUnavailable",
                                    )}
                                    onClick={() =>
                                      handleUpdateDraft(draft.id, {
                                        notifyEmail: !draft.notifyEmail,
                                      })
                                    }
                                  >
                                    <Mail size={14} />
                                  </ChannelToggle>
                                  <ChannelToggle
                                    label={t("pro.products.subscriptions.channel.sms")}
                                    active={draft.notifySms && channels.sms}
                                    dirty={channelDirty("notifySms")}
                                    disabled={saving || isRemoved}
                                    unavailable={!channels.sms}
                                    unavailableReason={
                                      selectedUser?.phone?.trim()
                                        ? t("pro.products.subscriptions.channel.smsUnavailable")
                                        : t("pro.products.subscriptions.channel.smsNoPhone")
                                    }
                                    onClick={() =>
                                      handleUpdateDraft(draft.id, { notifySms: !draft.notifySms })
                                    }
                                  >
                                    <MessageSquare size={14} />
                                  </ChannelToggle>
                                  <ChannelToggle
                                    label={t("pro.products.subscriptions.channel.web")}
                                    active={draft.notifyWeb}
                                    dirty={channelDirty("notifyWeb")}
                                    disabled={saving || isRemoved}
                                    onClick={() =>
                                      handleUpdateDraft(draft.id, { notifyWeb: !draft.notifyWeb })
                                    }
                                  >
                                    <Monitor size={14} />
                                  </ChannelToggle>
                                  <ChannelToggle
                                    label={t("pro.products.subscriptions.channel.push")}
                                    active={draft.notifyPush}
                                    dirty={channelDirty("notifyPush")}
                                    disabled={saving || isRemoved}
                                    onClick={() =>
                                      handleUpdateDraft(draft.id, { notifyPush: !draft.notifyPush })
                                    }
                                  >
                                    <BellRing size={14} />
                                  </ChannelToggle>
                                </div>
                                {draft.usesSeverity ? (
                                  <div className={s.notificationSeverity}>
                                    <label className={formStyles.label} style={{ margin: 0 }}>
                                      {t("pro.products.subscriptions.severity")}
                                    </label>
                                    <select
                                      className={clsx(
                                        formStyles.input,
                                        !isAdded &&
                                          !isRemoved &&
                                          base &&
                                          draft.minSeverity !== base.minSeverity &&
                                          s.dirtySelect,
                                      )}
                                      style={{ width: 100 }}
                                      value={draft.minSeverity}
                                      data-unsaved={
                                        !isAdded &&
                                        !isRemoved &&
                                        base &&
                                        draft.minSeverity !== base.minSeverity
                                          ? "true"
                                          : undefined
                                      }
                                      onChange={(e) =>
                                        handleUpdateDraft(draft.id, {
                                          minSeverity: e.target.value as MinSeverity,
                                        })
                                      }
                                      disabled={saving || isRemoved}
                                    >
                                      {SEVERITY_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                ) : null}
                              </div>

                              <div className={s.notificationRemove}>
                                <button
                                  type="button"
                                  className={s.removeButton}
                                  aria-label={
                                    isRemoved
                                      ? t("pro.products.subscriptions.undoRemove")
                                      : t("pro.products.subscriptions.remove")
                                  }
                                  title={
                                    isRemoved
                                      ? t("pro.products.subscriptions.undoRemove")
                                      : t("pro.products.subscriptions.remove")
                                  }
                                  disabled={saving}
                                  onClick={() => handleRemoveEvent(draft.id)}
                                >
                                  {isRemoved ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: "flex-end" }}>
              <div style={{ display: "flex", gap: 10 }}>
                <Button type="button" color="neutral" onClick={() => setOpen(false)} disabled={saving}>
                  {t("shared.common.cancel")}
                </Button>
                <Button type="submit" color="primary" disabled={saving || loading || !subscriberUserId || !hasProductAccess || !isDirty}>
                  {saving ? t("shared.common.saving") : t("shared.common.save")}
                </Button>
              </div>
            </Modal.Footer>
          </Modal.Form>
      </Modal>

      <Modal
        open={addEventModalOpen}
        setOpen={setAddEventModalOpen}
        minWidth="420px"
        maxWidth="520px"
        closeOnClickOff
      >
        <Modal.Header>
          <Text level="h2" weight="bold">
            {t("pro.products.subscriptions.selectEvent")}
          </Text>
          <Text
            level="body-small"
            style={{ color: "var(--color-fgMuted)", marginTop: 4, display: "block" }}
          >
            {`Notify ${subscriberLabel} when...`}
          </Text>
        </Modal.Header>
        <Modal.Body>
          {availableToAdd.length === 0 ? (
            <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
              {t("pro.products.subscriptions.allEventsAdded")}
            </Text>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {availableToAdd.map((item) => {
                if (item.kind === "composite") {
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={s.eventOption}
                      onClick={() => handleAddEvent(COMPOSITE_ALERT_PICKER_KEY)}
                    >
                      <span className={s.eventOptionTitle}>{COMPOSITE_ALERT_TITLE}</span>
                      <span className={s.eventOptionDescription}>
                        {COMPOSITE_ALERT_DESCRIPTION}
                      </span>
                    </button>
                  );
                }
                const description = pickerDescriptionFor(item.event);
                return (
                  <button
                    key={item.event.key}
                    type="button"
                    className={s.eventOption}
                    onClick={() => handleAddEvent(item.event.key)}
                  >
                    <span className={s.eventOptionTitle}>{pickerTitleFor(item.event)}</span>
                    {description ? (
                      <span className={s.eventOptionDescription}>{description}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "flex-end" }}>
          <Button type="button" color="neutral" onClick={() => setAddEventModalOpen(false)}>
            {t("shared.common.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {subscriberUserId && productId ? (
        <SubscriptionNotificationHistoryModal
          open={historyModalOpen}
          setOpen={setHistoryModalOpen}
          productId={productId}
          productName={productName}
          userId={subscriberUserId}
          userLabel={selectedUser ? userDisplayName(selectedUser) : "User"}
        />
      ) : null}
    </>
  );
}
